import { Badge, Callout, Card, CardHeader, Tooltip } from '@provenance/design';
import type { DocumentDetail } from '@provenance/shared';
import { AlertTriangle, PenLine } from 'lucide-react';

/**
 * The specimen signatures on file, beside the certificate — spec v4 §24.2, T-093.
 *
 * On an uploaded or emailed declaration this is the strongest check the chamber has: the
 * specimen on file is the only thing standing between a genuine submission and someone with
 * a copy of a company letterhead.
 *
 * **A human compares, not a score.** Signature similarity is a biometric judgement with a
 * false-positive rate, and the first time it is wrong it is wrong about someone's
 * livelihood (D-016, Q7). So the images go side by side and the operator decides, which is
 * fast, accurate and accountable.
 *
 * Where the signatory could not be read, every current specimen for the organisation is
 * shown rather than nothing — the operator can still recognise the right one.
 */
export function SignatureComparison({
  comparison,
}: {
  comparison: DocumentDetail['signatureComparison'];
}) {
  const { signedBy, specimens } = comparison;
  if (specimens.length === 0) return null;

  // The one the declaration names goes first; an operator should not have to hunt for it.
  const ordered = [...specimens].sort(
    (a, b) => Number(b.isNamedSignatory) - Number(a.isNamedSignatory),
  );
  const named = ordered.find((s) => s.isNamedSignatory);

  return (
    <Card>
      <CardHeader
        title="Signatures on file"
        description={
          signedBy
            ? `This certificate says it was signed by ${signedBy}. Compare it against the specimen.`
            : 'Who signed this certificate could not be read. Compare the signature on the document against these.'
        }
      />

      <div className="flex flex-col gap-3 p-4">
        {signedBy && !named && (
          <Callout
            tone="stop"
            icon={<AlertTriangle className="size-4" />}
            title={`${signedBy} is not a registered signatory`}
          >
            Nobody by that name is on file for this exporter. Either the certificate was
            signed by someone who is not authorised, or the exporter needs to register them.
          </Callout>
        )}

        <ul className="grid list-none gap-3 p-0 sm:grid-cols-2">
          {ordered.map((s) => (
            <li
              key={s.id}
              className={
                'rounded-[var(--pv-radius)] border p-3 ' +
                (s.isNamedSignatory
                  ? 'border-[var(--pv-leaf)] bg-[var(--pv-leaf-soft)]'
                  : 'border-[var(--pv-line)]')
              }
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="[font-size:13.5px] font-medium">{s.fullName}</p>
                  <p className="[font-size:12.5px] text-[var(--pv-muted)]">{s.position}</p>
                </div>
                {s.isNamedSignatory && (
                  <Badge tone="leaf" size="sm">
                    <PenLine aria-hidden className="size-2.5" />
                    Named on this certificate
                  </Badge>
                )}
                {s.lapsed && (
                  <Tooltip content="The validity window for this specimen has closed.">
                    <span>
                      <Badge tone="stop" size="sm">
                        Lapsed
                      </Badge>
                    </span>
                  </Tooltip>
                )}
              </div>

              {s.specimenUrl ? (
                <img
                  src={s.specimenUrl}
                  /* Described, not decorative: for a screen-reader user this image cannot
                     be compared, and the alternative text has to say what it is and whose
                     it is so they know to ask a colleague or use another route. */
                  alt={`Specimen signature on file for ${s.fullName}, ${s.position}`}
                  className="mt-2 h-24 w-full rounded-[var(--pv-radius-sm)] border border-[var(--pv-line)] bg-white object-contain p-2"
                />
              ) : (
                <p className="mt-2 flex h-24 items-center justify-center rounded-[var(--pv-radius-sm)] border border-dashed border-[var(--pv-line)] [font-size:12.5px] text-[var(--pv-faint)]">
                  No specimen provided
                </p>
              )}

              <p className="mt-1.5 [font-size:12px] text-[var(--pv-muted)]">
                {s.validUntil
                  ? `${s.lapsed ? 'Expired' : 'Valid until'} ${new Date(s.validUntil).toLocaleDateString('en-AU')}`
                  : 'No expiry recorded'}
              </p>
            </li>
          ))}
        </ul>

        <p className="[font-size:12.5px] text-[var(--pv-muted)]">
          Compare these against the signature on the certificate in the preview. Approving
          records that you were satisfied with the match.
        </p>
      </div>
    </Card>
  );
}
